import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { updateUser } from '../../../actions/user';
import NOTIFICATION_SETTINGS from '../../account/settings/notificationSettings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { styled } from '@mui/material/styles';

const StyledCreditCardIcon = styled(CreditCardIcon)(({ theme }) => ({
  fontSize: '2rem',
  marginRight: theme.spacing(1),
}));

const TopupForm = ({ user, primaryPaymentMethod, onFinish }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    threshold: user.topup_threshold,
    amount: user.topup_amount,
    notif_enabled: true,
    topup_enabled: Boolean(user.topup_threshold) || Boolean(user.topup_amount),
  });
  const [pristine, setPristine] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [thresholdError, setThresholdError] = useState(false);
  const [amountError, setAmountError] = useState(false);

  useEffect(() => {
    const top_up_setting = user.notification_settings?.find(
      (setting) => setting.key === NOTIFICATION_SETTINGS.AUTO_TOP_UP_EMAIL.key,
    );
    setFormData((prev) => ({
      ...prev,
      notif_enabled: top_up_setting ? top_up_setting.value : true,
    }));
  }, [user.notification_settings]);

  const handleSubmit = async () => {
    let notification_settings;
    if (!user.notification_settings) {
      notification_settings = [
        {
          key: NOTIFICATION_SETTINGS.AUTO_TOP_UP_EMAIL.key,
          value: formData.notif_enabled,
        },
      ];
    } else {
      notification_settings = user.notification_settings.filter(
        (setting) =>
          setting.key !== NOTIFICATION_SETTINGS.AUTO_TOP_UP_EMAIL.key,
      );
      notification_settings.push({
        key: NOTIFICATION_SETTINGS.AUTO_TOP_UP_EMAIL.key,
        value: formData.notif_enabled,
      });
    }

    setSubmitting(true);
    try {
      await dispatch(
        updateUser(
          formData.topup_enabled
            ? {
                topup_threshold: parseInt(formData.threshold),
                topup_amount: parseInt(formData.amount),
                notification_settings,
              }
            : {
                topup_threshold: null,
                topup_amount: null,
                notification_settings,
              },
        ),
      );
      setSubmitting(false);
      setPristine(true);
      if (onFinish) {
        onFinish();
      }
    } catch (error) {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setPristine(false);

    switch (name) {
      case 'threshold':
        setThresholdError(isNaN(value) || value < 0 || !value);
        break;
      case 'amount':
        setAmountError(isNaN(value) || value < 25 || !value);
        break;
      default:
        break;
    }
  };

  const handleCheckboxChange = (name) => (e) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
    setPristine(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant='h4' align='center' gutterBottom>
        Automatic Top-up
      </Typography>
      <Typography variant='body1' paragraph>
        Running jobs will automatically stop when you run out of credits. If you
        would like {import.meta.env.VITE_ORG_NAME} to automatically charge your
        primary payment method when your credits drop below a threshold, enter
        the threshold value and the amount of credits to be added. The minimum
        credits per transaction is 25 ($25).
      </Typography>

      <Box component='form' onSubmit={handleSubmit}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.topup_enabled}
              onChange={handleCheckboxChange('topup_enabled')}
            />
          }
          label='Enable automatic Top-up'
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.notif_enabled}
              onChange={handleCheckboxChange('notif_enabled')}
              disabled={!formData.topup_enabled}
            />
          }
          label='Send me an email notification when a top-up occurs'
        />

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={12}>
            <TextField
              fullWidth
              label='Top-up Credits Threshold'
              name='threshold'
              disabled={!formData.topup_enabled}
              placeholder={
                user.topup_threshold
                  ? `Current: ${user.topup_threshold} credits`
                  : 'Enter amount in credits'
              }
              value={formData.threshold}
              onChange={handleChange}
              error={thresholdError}
              helperText={
                thresholdError ? 'Please enter a valid number' : undefined
              }
            />
            {formData.threshold !== user.topup_threshold && (
              <Typography variant='body2' sx={{ mt: 1 }}>
                New threshold: {formData.threshold} credits, $
                {formData.threshold}
              </Typography>
            )}
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label='Top-up Credits Amount'
              name='amount'
              disabled={!formData.topup_enabled}
              placeholder={
                user.topup_amount
                  ? `Current: ${user.topup_amount} credits`
                  : 'Enter amount in credits'
              }
              value={formData.amount}
              onChange={handleChange}
              error={amountError}
              helperText={
                amountError ? 'Please enter a valid amount (>= 25)' : undefined
              }
            />
            {formData.amount !== user.topup_amount && (
              <Typography variant='body2' sx={{ mt: 1 }}>
                New amount: {formData.amount} credits, ${formData.amount}
              </Typography>
            )}
          </Grid>

          <Grid size={12}>
            <Typography variant='h6' gutterBottom>
              Default Payment Method
            </Typography>
            {primaryPaymentMethod ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StyledCreditCardIcon />
                <Box>
                  <Typography variant='body1'>
                    Card x{primaryPaymentMethod.card.last4}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Expires: {primaryPaymentMethod.card.exp_month}/
                    {primaryPaymentMethod.card.exp_year}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Alert severity='warning'>
                You have no primary payment method
              </Alert>
            )}
          </Grid>

          <Grid size={12}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSubmit}
              disabled={
                pristine ||
                thresholdError ||
                amountError ||
                !formData.threshold ||
                !formData.amount ||
                !primaryPaymentMethod ||
                submitting
              }
              loading={submitting}
            >
              Update
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default TopupForm;
